import { useState, useRef, useCallback } from 'react';
import { useApp } from '../../components/AppProvider';
import { Deal } from '../../lib/types';
import { supabase } from '../../lib/supabase';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { callWahaProxy } from '../../lib/services/wahaProxyService';
import { getUserWhatsAppConfig } from '../../lib/utils/getWhatsAppConfig';
import { useDealStore } from '../../lib/stores/dealStore';

// Helper function to extract file path from Supabase URL
function extractFilePathFromUrl(url: string): { bucketName: string; filePath: string } | null {
  try {
    // Parse the URL to extract bucket and path
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    
    // Expected format is /storage/v1/object/sign/bucket-name/path/to/file
    // or /storage/v1/object/public/bucket-name/path/to/file
    const parts = pathname.split('/');
    if (parts.length >= 7) {
      const bucketName = parts[5];
      const filePath = parts.slice(6).join('/');
      
      return {
        bucketName,
        filePath
      };
    }
    
    // If URL doesn't match expected pattern, return null
    return null;
  } catch (error) {
    console.error('Error parsing URL:', error);
    return null;
  }
}

interface UseContractUploadResult {
  contractFile: File | null;
  setContractFile: React.Dispatch<React.SetStateAction<File | null>>;
  uploadingContract: boolean;
  dragActive: boolean;
  setDragActive: React.Dispatch<React.SetStateAction<boolean>>;
  contractBucketName: string;
  contractFilePath: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  signingDate: Date;
  setSigningDate: React.Dispatch<React.SetStateAction<Date>>;
  showFileTooLargeModal: boolean;
  setShowFileTooLargeModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleContractUpload: (deal: Deal, updateDeal: Function, updateDealStatus: Function) => Promise<string | null | undefined>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  extractPathFromContractUrl: (url: string) => void;
}

export const useContractUpload = (translations: any): UseContractUploadResult => {
  const { toast } = useApp();
  const { settings } = useSettingsStore();
  
  // Contract upload states
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [contractBucketName, setContractBucketName] = useState<string>('');
  const [contractFilePath, setContractFilePath] = useState<string>('');
  const [signingDate, setSigningDate] = useState<Date>(new Date());
  const [showFileTooLargeModal, setShowFileTooLargeModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Extract path information from a contract URL
  // Memoizzato per evitare loop infiniti negli useEffect
  const extractPathFromContractUrl = useCallback((url: string) => {
    if (!url) return;

    const pathInfo = extractFilePathFromUrl(url);
    if (pathInfo) {
      setContractBucketName(pathInfo.bucketName);
      setContractFilePath(pathInfo.filePath);
    }
  }, []);

  // Contract upload handler
  const handleContractUpload = async (
    deal: Deal, 
    updateDeal: Function,
    updateDealStatus: Function
  ) => {
    if (!deal) return;
    if (!contractFile) {
      toast.error(translations.contract?.selectFile || 'Please select a file');
      return;
    }
    
    // Verify file type
    if (!contractFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error(translations.contract?.onlyPdf || 'Only PDF files are allowed');
      return;
    }

    // Verify file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    if (contractFile.size > MAX_FILE_SIZE) {
      setShowFileTooLargeModal(true);
      return;
    }

    setUploadingContract(true);
    try {
      let contractUrl = '';
      
      try {
        // Prepare file path
        const organizationId = deal.organization_id || 'shared';
        const safeName = contractFile.name
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // rimuove accenti (à→a, è→e)
          .replace(/\s+/g, '_')                                // spazi → underscore
          .replace(/[^a-zA-Z0-9._-]/g, '_')                   // caratteri non sicuri → underscore
          .replace(/_+/g, '_');                                // collassa underscore multipli
        const fileName = `${organizationId}/contracts/${deal.id}/${safeName}`;
        
        // Try to upload to 'contracts' bucket
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('contracts')
          .upload(fileName, contractFile, {
            upsert: true,
            contentType: 'application/pdf'
          });
          
        if (uploadError) {
          // If the contracts bucket doesn't exist, try with uploads bucket
          if (uploadError.message.includes('Bucket not found')) {
            const { data: uploadData2, error: uploadError2 } = await supabase.storage
              .from('uploads')
              .upload(fileName, contractFile, {
                upsert: true,
                contentType: 'application/pdf'
              });
              
            if (uploadError2) {
              throw uploadError2;
            }
            
            // Get the public URL from uploads bucket
            const { data: publicUrlData } = await supabase.storage
              .from('uploads')
              .getPublicUrl(fileName);
              
            contractUrl = publicUrlData.publicUrl;
            
            // Store the bucket and path for future URL refreshing
            setContractBucketName('uploads');
            setContractFilePath(fileName);
          } else {
            throw uploadError;
          }
        } else {
          // For contracts bucket, get a signed URL if possible
          try {
            // Try to get a signed URL (for private buckets)
            const { data: signedUrlData } = await supabase.storage
              .from('contracts')
              .createSignedUrl(fileName, 60 * 60 * 24); // 24 hour expiry
              
            if (signedUrlData?.signedUrl) {
              contractUrl = signedUrlData.signedUrl;
              
              // Store the bucket and path for future URL refreshing
              setContractBucketName('contracts');
              setContractFilePath(fileName);
            } else {
              // Fall back to public URL if signed URL isn't supported
              const { data: publicUrlData } = await supabase.storage
                .from('contracts')
                .getPublicUrl(fileName);
                
              contractUrl = publicUrlData.publicUrl;
              
              // Store the bucket and path for future URL refreshing
              setContractBucketName('contracts');
              setContractFilePath(fileName);
            }
          } catch (signedUrlError) {
            // If signed URLs aren't supported, fall back to public URL
            const { data: publicUrlData } = await supabase.storage
              .from('contracts')
              .getPublicUrl(fileName);
              
            contractUrl = publicUrlData.publicUrl;
            
            // Store the bucket and path for future URL refreshing
            setContractBucketName('contracts');
            setContractFilePath(fileName);
          }
        }
      } catch (storageError) {
        // Storage error - fail with clear message, no fallback
        console.error('Storage upload error:', storageError);
        const errorMessage = storageError instanceof Error ? storageError.message : 'Errore sconosciuto';

        // Check if error is due to file size
        if (errorMessage.toLowerCase().includes('maximum allowed size') ||
            errorMessage.toLowerCase().includes('too large') ||
            errorMessage.toLowerCase().includes('exceeded')) {
          setShowFileTooLargeModal(true);
        } else {
          toast.error(`Errore durante il caricamento del contratto: ${errorMessage}. Riprova più tardi.`);
        }

        setUploadingContract(false);
        return null;
      }
      
      // Validate signing date
      if (signingDate > new Date()) {
        toast.error('La data di firma non può essere nel futuro');
        setUploadingContract(false);
        return;
      }

      // FIX race condition (bug 2026-06-16): scrivere signed_contract_url PRIMA di
      // settare signed_at. Il DB trigger `deal_signed` scatta sulla transizione
      // signed_at NULL→valore (dentro updateDealStatus('accepted')); se a quel punto
      // il PDF non è ancora in DB, il workflow invia la notifica WhatsApp/email SENZA
      // allegato (l'enrichment legge signed_contract_url ancora NULL). Sequenza fix:
      //   1) scrivi il PDF (signed_at ancora null → il trigger non scatta)
      //   2) setta lo status accepted → il trigger scatta col PDF già presente
      //   3) correggi signed_at/closed_at alla data di firma scelta (valore→valore,
      //      non riscatta il trigger deal_signed che è NULL→valore)
      await updateDeal(deal.id, {
        signed_contract_url: contractUrl
      });

      await updateDealStatus(deal.id, 'accepted');

      await updateDeal(deal.id, {
        signed_at: signingDate.toISOString(),
        closed_at: signingDate.toISOString()
      });
      
      // Handle deal signed: creates projects, initializes renewals, creates tasks from templates, emits deal:signed event
      try {
        console.log(`[CONTRACT UPLOAD] Running handleDealSigned for deal ${deal.id}...`);
        await useDealStore.getState().handleDealSigned(deal.id);
        console.log(`[CONTRACT UPLOAD] ✅ handleDealSigned completed for deal: ${deal.id}`);
      } catch (signedError) {
        console.error(`[CONTRACT UPLOAD] ❌ Error in handleDealSigned for deal ${deal.id}:`, signedError);
        // Non-blocking error - il contratto è comunque caricato con successo
      }
      
      toast.success('Contratto caricato e accordo contrassegnato come accettato');
      
      // Send WhatsApp notification if configured
      if (settings?.whatsapp_notification_chat_id) {
        try {
          const chatId = settings.whatsapp_notification_chat_id;
          const clientName = deal.client?.business_name || 'Cliente';
          const dealTitle = deal.title || 'Preventivo';
          
          // Get services if available
          const services = Array.isArray(deal.deal_services) ? deal.deal_services : [];
          
          // Get the custom message or use default
          const defaultMessage = `🎉 Nuovo contratto firmato!\n\nCliente: ${clientName}\nPreventivo: ${dealTitle}\nImporto: €${deal.amount?.toFixed(2) || '0.00'}\n\nIl contratto è stato caricato con successo nel sistema.`;
          let message = settings.whatsapp_notification_message || defaultMessage;
          
          // Process variables in the message
          if (settings.whatsapp_notification_message) {
            // Replace basic variables
            message = message
              .replace(/{{clientName}}/g, clientName)
              .replace(/{{dealTitle}}/g, dealTitle)
              .replace(/{{dealAmount}}/g, (deal.amount?.toFixed(2) || '0.00'));
            
            // Process services section if exists
            const servicesRegex = /{{#services}}([\s\S]*?){{\/services}}/g;
            const servicesMatch = servicesRegex.exec(message);
            
            if (servicesMatch && servicesMatch[1] && services.length > 0) {
              const serviceTemplate = servicesMatch[1];
              const servicesList = services.map(service => {
                // Get service data safely
                const serviceTitle = service.service?.title || service.custom_service_name || 'Servizio';
                
                // Usa il prezzo scontato se disponibile, altrimenti il prezzo normale
                const hasDiscount = service.discounted_amount !== undefined && 
                                   service.discounted_amount !== null && 
                                   service.discounted_amount > 0;
                
                const serviceAmount = hasDiscount 
                  ? (service.discounted_amount?.toFixed(2) || '0.00')
                  : (service.amount?.toFixed(2) || '0.00');
                  
                const isMonthly = service.monthly_duration !== undefined && service.monthly_duration !== null;
                const serviceType = isMonthly ? 'Mensile' : 'Una tantum';
                
                // Replace variables in the service template
                const serviceText = serviceTemplate
                  .replace(/{{title}}/g, serviceTitle)
                  .replace(/{{amount}}/g, serviceAmount)
                  .replace(/{{type}}/g, serviceType);
                  
                return serviceText;
              }).join('');
              
              // Replace the services section with the processed list
              message = message.replace(servicesRegex, servicesList);
            }
          }
          
          // Prepariamo per inviare il messaggio WhatsApp
          try {
            // Otteniamo la configurazione di WhatsApp dall'utente
            const { config } = await getUserWhatsAppConfig();
            
            // Invio basato sul tipo di messaggio (con o senza allegato)
            if (settings.whatsapp_notification_attach_contract && contractUrl) {
              // Invio messaggio con allegato (file PDF). /api/sendMedia non esiste in WAHA 2026.10:
              // si usa /api/sendFile con file {url, mimetype, filename} (schema MessageFileRequest).
              const response = await callWahaProxy({
                method: 'POST',
                path: '/api/sendFile',
                body: {
                  session: config.configuration.session,
                  chatId: chatId,
                  file: {
                    url: contractUrl,
                    mimetype: 'application/pdf',
                    filename: contractFile.name,
                  },
                  caption: message,
                },
              });
              
              if (!response.ok) {
                throw new Error(`Errore nell'invio del messaggio con allegato: ${response.status} ${response.statusText}`);
              }
              
              console.log('WhatsApp notification with attachment sent successfully');
            } else {
              // Invio messaggio di solo testo
              const response = await callWahaProxy({
                method: 'POST',
                path: '/api/sendText',
                body: {
                  session: config.configuration.session,
                  chatId: chatId,
                  text: message,
                  linkPreview: true,
                },
              });
              
              if (!response.ok) {
                throw new Error(`Errore nell'invio del messaggio: ${response.status} ${response.statusText}`);
              }
              
              console.log('WhatsApp notification sent successfully');
            }
          } catch (err) {
            console.error('Failed to send WhatsApp notification:', err);
            // Il contratto e' caricato: si avvisa solo che la notifica WhatsApp non e' partita.
            toast.error(`Contratto caricato, ma la notifica WhatsApp non è partita: ${err instanceof Error ? err.message : 'errore sconosciuto'}`);
          }
        } catch (notificationError) {
          console.error('Failed to send WhatsApp notification:', notificationError);
          toast.error('Contratto caricato, ma la notifica WhatsApp non è partita');
        }
      }
      
      // Clear the file input
      setContractFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      return contractUrl;
    } catch (error) {
      console.error('Error uploading contract:', error);
      toast.error('Failed to upload contract: ' + (error instanceof Error ? error.message : ''));
      return null;
    } finally {
      setUploadingContract(false);
    }
  };

  // Contract file change handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setContractFile(e.target.files[0]);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        setContractFile(file);
      } else {
        toast.error(translations.contract?.onlyPdf || 'Only PDF files are allowed');
      }
    }
  };

  return {
    contractFile,
    setContractFile,
    uploadingContract,
    dragActive,
    setDragActive,
    contractBucketName,
    contractFilePath,
    fileInputRef,
    signingDate,
    setSigningDate,
    showFileTooLargeModal,
    setShowFileTooLargeModal,
    handleContractUpload,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    extractPathFromContractUrl
  };
};